import { api } from "@/lib/apiClient";
import {
  paymentMethodLabel,
  paymentMethodUsesBank,
  type ExpensePaymentMethod,
} from "@/lib/expenses/expensesStore";
import { isAxiosError } from "axios";

export type SalaryAdvanceLineStatus = "active" | "deducted" | "cancelled";

export type EmployeeSalaryAdvanceLine = {
  id: string;
  batchId: string;
  employeeId: string;
  employeeName: string | null;
  advanceAmount: string;
  advanceDate: string | null;
  periodYear: number;
  periodMonth: number;
  reason: string | null;
  paymentMethod: ExpensePaymentMethod | "";
  paymentMethodLabel: string;
  bankId: string;
  bankName: string | null;
  referenceNo: string | null;
  chequeNo: string | null;
  chequeDate: string | null;
  status: SalaryAdvanceLineStatus;
  salaryId: string | null;
  deductedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AdvanceSalaryBatch = {
  batchId: string;
  employeeId: string;
  employeeName: string | null;
  lines: EmployeeSalaryAdvanceLine[];
  totalAmount: number;
  canDelete: boolean;
  createdAt: string;
  updatedAt: string;
};

/** Advances grouped by employee (+ optional salary period). */
export type AdvanceEmployeeMonthGroup = {
  key: string;
  employeeId: string;
  employeeName: string | null;
  /** Null when grouped by employee only (all periods). */
  periodYear: number | null;
  /** Null when grouped by employee only (all periods). */
  periodMonth: number | null;
  lines: EmployeeSalaryAdvanceLine[];
  batchIds: string[];
  totalAmount: number;
  canDelete: boolean;
  createdAt: string;
  updatedAt: string;
};

export type AdvanceSalaryFormMeta = {
  grossSalary: number;
  blockedPeriods: Array<{ year: number; month: number }>;
  reservedPeriods: Array<{ year: number; month: number }>;
  periodAdvances: Array<{ year: number; month: number; total: number; count: number }>;
};

type PaginatedResponse = {
  data?: Record<string, unknown>[];
  meta?: { current_page?: number; last_page?: number; per_page?: number; total?: number };
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapStatus(raw: unknown): SalaryAdvanceLineStatus {
  const s = str(raw);
  if (s === "deducted") return "deducted";
  if (s === "cancelled") return "cancelled";
  return "active";
}

function mapPaymentMethod(raw: unknown): ExpensePaymentMethod | "" {
  const s = str(raw);
  if (s === "cash" || s === "online" || s === "cheque" || s === "pay_order" || s === "cash_deposit") return s;
  return "";
}

function mapLine(raw: Record<string, unknown>): EmployeeSalaryAdvanceLine {
  const paymentMethod = mapPaymentMethod(raw.payment_method);
  return {
    id: str(raw.id),
    batchId: str(raw.batch_id),
    employeeId: str(raw.employee_id),
    employeeName: raw.employee_name != null ? str(raw.employee_name) : null,
    advanceAmount: str(raw.advance_amount),
    advanceDate: raw.advance_date != null ? str(raw.advance_date) : null,
    periodYear: Number(raw.period_year) || 0,
    periodMonth: Number(raw.period_month) || 0,
    reason: raw.reason != null ? str(raw.reason) : null,
    paymentMethod,
    paymentMethodLabel: str(raw.payment_method_label) || paymentMethodLabel(paymentMethod),
    bankId: raw.bank_id != null ? str(raw.bank_id) : "",
    bankName: raw.bank_name != null ? str(raw.bank_name) : null,
    referenceNo: raw.reference_no != null ? str(raw.reference_no) : null,
    chequeNo: raw.cheque_no != null ? str(raw.cheque_no) : null,
    chequeDate: raw.cheque_date != null ? str(raw.cheque_date) : null,
    status: mapStatus(raw.status),
    salaryId: raw.salary_id != null ? str(raw.salary_id) : null,
    deductedAt: raw.deducted_at != null ? str(raw.deducted_at) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

/** Human-readable payment destination for listing cards. */
export function paymentDestinationLabel(line: Pick<
  EmployeeSalaryAdvanceLine,
  "paymentMethod" | "paymentMethodLabel" | "bankName" | "referenceNo" | "chequeNo" | "chequeDate"
>): string {
  const method = line.paymentMethodLabel || paymentMethodLabel(line.paymentMethod);
  if (!line.paymentMethod || line.paymentMethod === "cash") {
    return method || "Cash";
  }

  const parts = [method];
  if (line.bankName) parts.push(line.bankName);
  if (line.paymentMethod === "cheque") {
    if (line.chequeNo) parts.push(`Chq ${line.chequeNo}`);
    if (line.chequeDate) parts.push(line.chequeDate);
  } else if (line.referenceNo) {
    parts.push(`Ref ${line.referenceNo}`);
  }
  return parts.filter(Boolean).join(" · ");
}

export function groupAdvanceSalaryBatches(lines: EmployeeSalaryAdvanceLine[]): AdvanceSalaryBatch[] {
  const map = new Map<string, EmployeeSalaryAdvanceLine[]>();
  for (const line of lines) {
    const key = line.batchId || line.id;
    const bucket = map.get(key) ?? [];
    bucket.push(line);
    map.set(key, bucket);
  }

  return Array.from(map.entries())
    .map(([batchId, batchLines]) => {
      const sorted = [...batchLines].sort((a, b) => a.id.localeCompare(b.id));
      const totalAmount = sorted.reduce((sum, row) => sum + (Number(row.advanceAmount.replace(/,/g, "")) || 0), 0);
      const canDelete = sorted.every((row) => row.status !== "deducted");
      const updatedAt = sorted.reduce((max, row) => (row.updatedAt > max ? row.updatedAt : max), sorted[0]?.updatedAt ?? "");
      const createdAt = sorted.reduce((min, row) => (row.createdAt < min ? row.createdAt : min), sorted[0]?.createdAt ?? "");
      const head = sorted[0];

      return {
        batchId,
        employeeId: head?.employeeId ?? "",
        employeeName: head?.employeeName ?? null,
        lines: sorted,
        totalAmount,
        canDelete,
        createdAt,
        updatedAt,
      };
    })
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function groupAdvancesByEmployeeMonth(lines: EmployeeSalaryAdvanceLine[]): AdvanceEmployeeMonthGroup[] {
  const map = new Map<string, EmployeeSalaryAdvanceLine[]>();
  for (const line of lines) {
    const key = `${line.employeeId}|${line.periodYear}-${String(line.periodMonth).padStart(2, "0")}`;
    const bucket = map.get(key) ?? [];
    bucket.push(line);
    map.set(key, bucket);
  }

  return Array.from(map.entries())
    .map(([key, groupLines]) => buildAdvanceGroup(key, groupLines, true))
    .sort((a, b) => {
      const periodCmp = periodKey(b.periodYear ?? 0, b.periodMonth ?? 0).localeCompare(
        periodKey(a.periodYear ?? 0, a.periodMonth ?? 0),
      );
      if (periodCmp !== 0) return periodCmp;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
}

/** Group advances by employee only (all salary periods combined). */
export function groupAdvancesByEmployee(lines: EmployeeSalaryAdvanceLine[]): AdvanceEmployeeMonthGroup[] {
  const map = new Map<string, EmployeeSalaryAdvanceLine[]>();
  for (const line of lines) {
    const key = line.employeeId || line.id;
    const bucket = map.get(key) ?? [];
    bucket.push(line);
    map.set(key, bucket);
  }

  return Array.from(map.entries())
    .map(([key, groupLines]) => buildAdvanceGroup(key, groupLines, false))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

function buildAdvanceGroup(
  key: string,
  groupLines: EmployeeSalaryAdvanceLine[],
  includePeriod: boolean,
): AdvanceEmployeeMonthGroup {
  const sorted = [...groupLines].sort((a, b) => {
    const periodCmp = periodKey(b.periodYear, b.periodMonth).localeCompare(periodKey(a.periodYear, a.periodMonth));
    if (periodCmp !== 0) return periodCmp;
    const byDate = String(b.advanceDate ?? "").localeCompare(String(a.advanceDate ?? ""));
    if (byDate !== 0) return byDate;
    return b.id.localeCompare(a.id);
  });
  const totalAmount = sorted.reduce((sum, row) => sum + (Number(row.advanceAmount.replace(/,/g, "")) || 0), 0);
  const canDelete = sorted.every((row) => row.status !== "deducted");
  const updatedAt = sorted.reduce((max, row) => (row.updatedAt > max ? row.updatedAt : max), sorted[0]?.updatedAt ?? "");
  const createdAt = sorted.reduce((min, row) => (row.createdAt < min ? row.createdAt : min), sorted[0]?.createdAt ?? "");
  const head = sorted[0];
  const batchIds = Array.from(new Set(sorted.map((row) => row.batchId || row.id).filter(Boolean)));

  return {
    key,
    employeeId: head?.employeeId ?? "",
    employeeName: head?.employeeName ?? null,
    periodYear: includePeriod ? (head?.periodYear ?? 0) : null,
    periodMonth: includePeriod ? (head?.periodMonth ?? 0) : null,
    lines: sorted,
    batchIds,
    totalAmount,
    canDelete,
    createdAt,
    updatedAt,
  };
}

export function salaryAdvancesApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export async function listSalaryAdvancesForEmployee(params: {
  employeeId: string;
  page?: number;
  perPage?: number;
}): Promise<{ rows: EmployeeSalaryAdvanceLine[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>(`/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-advances`, {
    params: {
      page: params.page,
      per_page: params.perPage,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapLine(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function listAllSalaryAdvances(params: {
  page?: number;
  perPage?: number;
}): Promise<{ rows: EmployeeSalaryAdvanceLine[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>("/api/v1/salary-advances", {
    params: {
      page: params.page,
      per_page: params.perPage,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapLine(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function fetchAdvanceSalaryFormMeta(employeeId: string): Promise<AdvanceSalaryFormMeta> {
  const response = await api.get<{ data?: Record<string, unknown> }>("/api/v1/salary-advances/form-meta", {
    params: { employee_id: employeeId },
  });
  const raw = response.data?.data ?? {};
  const blocked = Array.isArray(raw.blocked_periods) ? raw.blocked_periods : [];
  const reserved = Array.isArray(raw.reserved_periods) ? raw.reserved_periods : [];
  const periodAdvances = Array.isArray(raw.period_advances) ? raw.period_advances : [];

  return {
    grossSalary: Number(raw.gross_salary) || 0,
    blockedPeriods: blocked.map((p) => ({
      year: Number((p as { year?: unknown }).year) || 0,
      month: Number((p as { month?: unknown }).month) || 0,
    })),
    reservedPeriods: reserved.map((p) => ({
      year: Number((p as { year?: unknown }).year) || 0,
      month: Number((p as { month?: unknown }).month) || 0,
    })),
    periodAdvances: periodAdvances.map((p) => ({
      year: Number((p as { year?: unknown }).year) || 0,
      month: Number((p as { month?: unknown }).month) || 0,
      total: Number((p as { total?: unknown }).total) || 0,
      count: Number((p as { count?: unknown }).count) || 0,
    })),
  };
}

export async function createAdvanceSalaryBatch(params: {
  employeeId: string;
  lines: Array<{
    advanceAmount: number | string;
    advanceDate: string;
    periodYear: number;
    periodMonth: number;
    reason?: string | null;
  }>;
  paymentMethod: string;
  bankId: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
}): Promise<{ lines: EmployeeSalaryAdvanceLine[]; message: string }> {
  const usesBank = paymentMethodUsesBank(params.paymentMethod as ExpensePaymentMethod);
  const response = await api.post<{ message?: string; data?: Record<string, unknown>[] }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-advances`,
    {
      lines: params.lines.map((line) => ({
        advance_amount: line.advanceAmount,
        advance_date: line.advanceDate,
        period_year: line.periodYear,
        period_month: line.periodMonth,
        reason: line.reason?.trim() || null,
      })),
      payment_method: params.paymentMethod || null,
      bank_id: usesBank && params.bankId ? Number(params.bankId) : null,
      reference_no:
        params.paymentMethod !== "cheque" && params.paymentMethod !== "cash"
          ? params.referenceNo.trim() || null
          : null,
      cheque_no: params.paymentMethod === "cheque" ? params.chequeNo.trim() || null : null,
      cheque_date: params.paymentMethod === "cheque" ? params.chequeDate.trim() || null : null,
    },
  );
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    lines: list.map((r) => mapLine(r as Record<string, unknown>)),
    message: str(response.data?.message) || "Advance salary recorded.",
  };
}

export async function deleteAdvanceSalaryBatch(params: {
  employeeId: string;
  batchId: string;
}): Promise<{ message: string }> {
  const response = await api.delete<{ message?: string }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-advances/batch/${encodeURIComponent(params.batchId)}`,
  );
  return {
    message: str(response.data?.message) || "Advance salary record deleted.",
  };
}

export function periodKey(year: number | null | undefined, month: number | null | undefined): string {
  return `${year ?? 0}-${String(month ?? 0).padStart(2, "0")}`;
}

export function periodLabel(
  year: number | null | undefined,
  month: number | null | undefined,
  months: readonly { v: number; label: string }[],
): string {
  if (year == null || month == null) return "All periods";
  const m = months.find((x) => x.v === month);
  return `${m?.label ?? month} ${year}`;
}
